/**
 * ExistingTicketCard — displays an existing engineering ticket in the
 * right-pane (or inline as a compact card) when the agent calls
 * `show_ticket`.
 *
 * Visually distinct from SubmittedTicketCard: rather than the
 * "just-created success" framing, this is an "existing record" framing
 * — informational, with an "Existing ticket" eyebrow and an outbound
 * link to Asana. No primary CTA (the user is reading, not deciding).
 */

import type { CSSProperties, ReactNode } from 'react'
import { Archive, ExternalLink, User } from 'lucide-react'
import type { ExistingTicketPayload } from '../types'
import {
  formatTicketType,
  ticketTypeAccent,
  severityAccent,
  formatSeverityLabel,
} from './ticket-visuals'

interface ExistingTicketCardProps {
  data: ExistingTicketPayload
  variant?: 'full' | 'compact'
}

function formatCreatedAt(ts: number | null | undefined): string {
  if (!ts || typeof ts !== 'number') return ''
  try {
    const date = new Date(ts * 1000)
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return ''
  }
}

export function ExistingTicketCard({
  data,
  variant = 'full',
}: ExistingTicketCardProps): ReactNode {
  const isFull = variant === 'full'
  const typeAccent = ticketTypeAccent(data.type)
  const sevAccent = severityAccent(data.severity)
  const createdLabel = formatCreatedAt(data.created_at ?? null)

  return (
    <div
      style={{
        ...styles.card,
        ...(isFull ? styles.cardFull : styles.cardCompact),
      }}
      data-testid={`existing-ticket-card-${variant}`}
    >
      {/* Neutral rail across top — not success-tinted */}
      <div style={styles.rail} />

      {/* Hero: archive icon + ticket id */}
      <div style={isFull ? styles.heroFull : styles.heroCompact}>
        <div style={styles.idRing}>
          <Archive
            size={isFull ? 26 : 18}
            color="var(--text-secondary)"
            strokeWidth={2}
          />
        </div>
        <div style={styles.heroText}>
          <span style={styles.eyebrow}>Existing Ticket</span>
          <span
            style={{
              ...styles.ticketId,
              ...(isFull ? styles.ticketIdFull : styles.ticketIdCompact),
            }}
            data-testid="existing-ticket-id"
          >
            {data.ticket_id}
          </span>
        </div>
      </div>

      {/* Pills */}
      <div style={styles.pillRow}>
        {data.type ? (
          <span
            style={{
              ...styles.pill,
              color: typeAccent.fg,
              background: typeAccent.bg,
              border: `1px solid ${typeAccent.border}`,
            }}
          >
            <span style={{ ...styles.pillDot, background: typeAccent.fg }} />
            {formatTicketType(data.type)}
          </span>
        ) : null}
        {data.severity ? (
          <span
            style={{
              ...styles.pill,
              color: sevAccent.fg,
              background: sevAccent.bg,
              border: `1px solid ${sevAccent.border}`,
            }}
          >
            {formatSeverityLabel(data.severity)}
          </span>
        ) : null}
        {data.status ? (
          <span style={styles.statusPill}>{data.status.replace(/_/g, ' ')}</span>
        ) : null}
      </div>

      {/* Title */}
      <h2 style={isFull ? styles.titleFull : styles.titleCompact}>{data.title}</h2>

      {/* Description */}
      <div
        style={{
          ...styles.descriptionBlock,
          ...(isFull ? styles.descriptionFull : styles.descriptionCompact),
        }}
      >
        <span style={styles.sectionLabel}>Description</span>
        <p
          style={{
            ...styles.descriptionText,
            ...(isFull ? styles.descriptionTextFull : {}),
          }}
        >
          {data.description || '(no description)'}
        </p>
      </div>

      {/* Metadata */}
      <div style={styles.metaGrid}>
        <MetaItem label="Entity" value={data.entity_id || '—'} mono />
        {data.affected_area ? (
          <MetaItem label="Affected area" value={data.affected_area} mono />
        ) : null}
        <MetaItem
          label="Reporter"
          value={
            data.reporter ? (
              <span style={styles.reporter}>
                <User size={12} style={styles.reporterIcon} />
                {data.reporter}
              </span>
            ) : (
              <span style={styles.metaEmpty}>—</span>
            )
          }
        />
        {createdLabel ? (
          <MetaItem label="Created" value={createdLabel} />
        ) : null}
        {typeof data.report_count === 'number' && data.report_count > 0 ? (
          <MetaItem
            label="Customer reports"
            value={String(data.report_count)}
          />
        ) : null}
        <MetaItem
          label="Tags"
          value={
            data.tags && data.tags.length > 0 ? (
              <span style={styles.tagRow}>
                {data.tags.map((tag) => (
                  <span key={tag} style={styles.tagChip}>
                    {tag}
                  </span>
                ))}
              </span>
            ) : (
              <span style={styles.metaEmpty}>—</span>
            )
          }
        />
      </div>

      {/* Footer — outbound link only, no primary CTA */}
      {data.asana_url ? (
        <div style={styles.footer}>
          <a
            href={data.asana_url}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.linkAction}
            data-testid="existing-ticket-asana-link"
          >
            <ExternalLink size={13} />
            Open in Asana
          </a>
        </div>
      ) : null}
    </div>
  )
}

// ── Sub-components ───────────────────────────────────────────────────────────

function MetaItem({
  label,
  value,
  mono = false,
}: {
  label: string
  value: ReactNode
  mono?: boolean
}) {
  return (
    <div style={styles.metaItem}>
      <span style={styles.metaLabel}>{label}</span>
      <span
        style={{
          ...styles.metaValue,
          ...(mono ? styles.metaValueMono : {}),
        }}
      >
        {value}
      </span>
    </div>
  )
}

// ── Styles ───────────────────────────────────────────────────────────────────

const NEUTRAL_TINT = 'rgba(148, 163, 184, 0.22)'

const styles: Record<string, CSSProperties> = {
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
    boxShadow: `0 4px 18px rgba(0, 0, 0, 0.18), 0 0 0 1px ${NEUTRAL_TINT}`,
  },
  cardFull: {
    gap: '20px',
    padding: '24px',
  },
  cardCompact: {
    gap: '12px',
    padding: '14px',
  },
  rail: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background:
      'linear-gradient(90deg, transparent 0%, var(--text-muted) 50%, transparent 100%)',
    opacity: 0.5,
  },
  heroFull: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  heroCompact: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  idRing: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    background: 'var(--bg-tertiary)',
    width: '46px',
    height: '46px',
    borderRadius: '50%',
    border: '1px solid var(--border-subtle)',
  },
  heroText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
  },
  eyebrow: {
    fontSize: '10.5px',
    fontWeight: 700,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  ticketId: {
    fontFamily: "'JetBrains Mono', monospace",
    color: 'var(--text-primary)',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  ticketIdFull: {
    fontSize: '20px',
  },
  ticketIdCompact: {
    fontSize: '14px',
  },
  pillRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
  },
  pillDot: {
    display: 'inline-block',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
  },
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    color: 'var(--text-muted)',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
  },
  titleFull: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
    color: 'var(--text-primary)',
  },
  titleCompact: {
    margin: 0,
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: 1.3,
    color: 'var(--text-primary)',
  },
  descriptionBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
  },
  descriptionFull: {
    padding: '14px 16px',
  },
  descriptionCompact: {
    padding: '10px 12px',
  },
  sectionLabel: {
    fontSize: '10px',
    fontWeight: 600,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  descriptionText: {
    margin: 0,
    fontSize: '13px',
    lineHeight: 1.55,
    color: 'var(--text-primary)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  descriptionTextFull: {
    fontSize: '14px',
    lineHeight: 1.65,
  },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '14px 20px',
  },
  metaItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
  },
  metaLabel: {
    fontSize: '10px',
    fontWeight: 600,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  metaValue: {
    fontSize: '13px',
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  metaValueMono: {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '12px',
  },
  metaEmpty: {
    color: 'var(--text-muted)',
  },
  reporter: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  reporterIcon: {
    color: 'var(--text-muted)',
  },
  tagRow: {
    display: 'inline-flex',
    flexWrap: 'wrap',
    gap: '6px',
    whiteSpace: 'normal',
  },
  tagChip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    fontSize: '11px',
    color: 'var(--text-secondary)',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: '999px',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '12px',
    paddingTop: '12px',
    borderTop: '1px solid var(--border-subtle)',
  },
  linkAction: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--accent-primary)',
    background: 'color-mix(in srgb, var(--accent-primary) 8%, transparent)',
    borderRadius: 'var(--radius-sm)',
    textDecoration: 'none',
    transition: 'background var(--transition-fast)',
  },
}

export default ExistingTicketCard
